import axios from 'axios';

export type ActivityItemType =
  | 'record_created'
  | 'record_edited'
  | 'record_deleted'
  | 'session_completed'
  | 'session_deleted'
  | 'session_signed_off'
  | 'session_signoff_revoked'
  | 'session_marked_incomplete'
  | 'session_incomplete'
  | 'session_cancelled'
  | 'combo_started'
  | 'combo_completed'
  | 'combo_marked_incomplete'
  | 'combo_cancelled'
  | 'program_created'
  | 'program_deleted';

export interface ActivityItem {
  id: string;
  module: 'scouting' | 'spraying';
  type: ActivityItemType;
  verb: string;
  actor: string;
  object: string;
  timestamp: string;
  deepLink: string;
}

/** Trimmed combo shape attached when GET /sessions?includeCombos=true. */
export interface TodaySessionCombo {
  _id: string;
  operatorId: string;
  status: string;
}

export interface TodaySession {
  _id: string;
  /** Auto-generated as `[programName] - [orchardName]` on creation; users may rename. */
  name?: string;
  /** Tenant-scoped display sequence (`SES-{seq}`); legacy sessions may lack this. */
  seq?: number;
  /** Single-orchard invariant for new sessions, but legacy rows may carry multiple. */
  orchardIds?: string[];
  /** Planned date (canonical session date). */
  date: string;
  /** Actual start (set when execution begins; null on planned sessions). */
  startDate?: string | null;
  /** Actual end (set when execution finishes; null on planned/in-progress sessions). */
  endDate?: string | null;
  status: string;
  operatorIds?: string[];
  /**
   * Per-operator/machine combo rollup, populated only when the list endpoint
   * is called with `?includeCombos=true`. Used by the Home page recent-sessions
   * card to render "N operators · X completed, Y in progress" per session.
   * Absent on legacy single-operator sessions or when the flag is off.
   */
  combos?: TodaySessionCombo[];
}

export interface TodayScoutingRecord {
  _id: string;
  blockId: string;
  date: string;
  source: string;
  metadata?: { scouterName?: string };
}

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchActivity(days = 7, limit = 12): Promise<ActivityItem[]> {
  const res = await axios.get('/api/v1/activity', {
    params: { days, limit },
    headers: authHeader(),
  });
  return res.data;
}

function isoDateNDaysFromNow(offsetDays: number): string {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.toISOString();
}

/**
 * Fetches sessions whose lifecycle date (status-aware: endDate for completed,
 * startDate for in-progress, `date` otherwise) falls within `[now - daysBack, now + daysForward]`.
 *
 * Uses the `lifecycleFrom` / `lifecycleTo` server params introduced for the Home
 * page so that a session planned months ago but completed yesterday still
 * surfaces. The plain `dateFrom` / `dateTo` params filter on the planned date
 * only and are unsuitable for "recent activity" purposes.
 */
export async function fetchRecentSpraySessions(daysBack = 7, daysForward = 7): Promise<TodaySession[]> {
  const lifecycleFrom = isoDateNDaysFromNow(-daysBack);
  const lifecycleTo   = isoDateNDaysFromNow(daysForward);
  const res = await axios.get('/api/v1/spray/sessions', {
    // includeCombos: opt-in flag that asks the backend to attach the
    // OperatorSpraySession combos for each session, used by the Home page
    // recent-sessions card to render a per-session operator rollup.
    params: { lifecycleFrom, lifecycleTo, includeCombos: true },
    headers: authHeader(),
  });
  return res.data;
}

export async function fetchRecentScouting(daysBack = 7): Promise<TodayScoutingRecord[]> {
  const startDate = isoDateNDaysFromNow(-daysBack);
  const endDate   = isoDateNDaysFromNow(0);
  const res = await axios.get('/api/v1/scouting/', {
    params: { startDate, endDate },
    headers: authHeader(),
  });
  return res.data;
}
